import React from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArticleIcon from '@mui/icons-material/Article';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import HubIcon from '@mui/icons-material/Hub';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import WebIcon from '@mui/icons-material/Web';
import { Box, Button, Card, CardContent, Chip, Container, Grid, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import cmsNetworkLarge from '../assets/images/cms-network-1536.webp';
import cmsNetworkSmall from '../assets/images/cms-network-768.webp';
import BookCallLink from '../components/BookCallLink';
import TestimonialQuote from '../components/TestimonialQuote';
import { testimonials } from '../data/Testimonials';

const capabilities = [
  {
    title: 'Content Platforms & CMS',
    icon: <ArticleIcon sx={{ fontSize: 32, color: '#005596' }} />,
    description:
      'Our principal has built publishing tools for distinct sites, languages, and editorial teams.',
    tech: ['Multi-site CMS', 'Editorial Roles', 'Multilingual Content'],
    bullets: [
      'Shared platform, distinct site identities',
      'Pages, posts, media, and event content',
      'Publishing tools for nontechnical staff',
    ],
  },
  {
    title: 'Custom Web Applications',
    icon: <WebIcon sx={{ fontSize: 32, color: '#005596' }} />,
    description:
      'Portals and internal tools that make everyday work easier for staff and customers.',
    tech: ['React', 'TypeScript', 'Next.js', '.NET', 'APIs'],
    bullets: [
      'Administrative portals and approval workflows',
      'Clear, accessible interfaces',
      'Inquiry and availability workflows',
    ],
  },
  {
    title: 'Deployment & Operations',
    icon: <CloudQueueIcon sx={{ fontSize: 32, color: '#005596' }} />,
    description:
      'Build, deployment, and hosting plans that make software easier to release and maintain.',
    tech: ['Docker', 'Cloudflare', 'Vercel', 'CI/CD'],
    bullets: [
      'Repeatable builds and deployments',
      'Hosting choices that fit the application',
      'Clear handoffs for ongoing support',
    ],
  },
  {
    title: 'Data & Integrations',
    icon: <StorageIcon sx={{ fontSize: 32, color: '#005596' }} />,
    description: 'Connect applications to existing data, services, and the people who need them.',
    tech: ['PostgreSQL', 'MySQL', 'SQL Server', 'XML', 'REST APIs'],
    bullets: [
      'Data models built around real workflows',
      'APIs and external data feeds',
      'Search, reporting, and content management',
    ],
  },
];

const Home: React.FC = () => {
  return (
    <Container
      maxWidth='lg'
      sx={{ pt: { xs: 4, md: 6 } }}
    >
      {/* 1. HERO SECTION */}
      <Box sx={{ mb: { xs: 8, md: 12 } }}>
        <Grid
          container
          spacing={{ xs: 4, lg: 6 }}
          alignItems='center'
        >
          {/* Left Column: Executive Framing */}
          <Grid
            item
            xs={12}
            lg={7}
          >
            {/* Eyebrow Badge */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 0.75,
                borderRadius: 50,
                backgroundColor: '#EFF6FF',
                border: '1px solid rgba(0, 85, 150, 0.2)',
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#00B4D8',
                }}
              />
              <Typography
                variant='caption'
                sx={{
                  fontFamily: '"Inter Variable", sans-serif',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  color: '#005596',
                  textTransform: 'uppercase',
                  fontSize: '11px',
                }}
              >
                Content Platforms &amp; Custom Software
              </Typography>
            </Box>

            {/* Monolithic H1 (ADA Landmark) */}
            <Typography
              variant='h1'
              component='h1'
              sx={{
                fontSize: { xs: '2.25rem', sm: '3rem', md: '3.5rem' },
                lineHeight: { xs: 1.15, md: 1.1 },
                fontWeight: 800,
                color: '#0B2545',
                letterSpacing: '-0.03em',
                mb: 2.5,
              }}
            >
              Make content easier to publish, approve, and maintain.
            </Typography>

            <Typography
              variant='body1'
              sx={{
                fontSize: { xs: '1rem', md: '1.15rem' },
                color: '#475569',
                lineHeight: 1.7,
                maxWidth: 600,
                mb: 4,
              }}
            >
              Scaled Tech builds content platforms and custom applications around your team&apos;s
              workflow. We help connect editors, approval processes, and existing systems, with
              direct access to the principal responsible for technical direction.
            </Typography>

            {/* Dual CTAs */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
              <Button
                component={Link}
                to='/case-studies'
                variant='contained'
                size='large'
                endIcon={<HubIcon />}
                sx={{
                  backgroundColor: '#0B2545',
                  color: '#FFFFFF',
                  px: 3.5,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '15px',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: '#005596',
                  },
                }}
              >
                Read the case studies
              </Button>
              <Button
                component={Link}
                to='/contact-us'
                data-track='discuss_project'
                variant='outlined'
                size='large'
                startIcon={<ArrowForwardIcon sx={{ color: '#005596' }} />}
                sx={{
                  borderColor: 'rgba(11, 37, 69, 0.25)',
                  color: '#0B2545',
                  px: 3.5,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '15px',
                  borderRadius: 2,
                  backgroundColor: '#FFFFFF',
                  '&:hover': {
                    borderColor: '#005596',
                    backgroundColor: '#F8FAFC',
                    color: '#005596',
                  },
                }}
              >
                Discuss your project
              </Button>
            </Box>

            {/* Trust Signals */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 3,
                pt: 2,
                borderTop: '1px solid rgba(15, 23, 42, 0.08)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 18, color: '#005596' }} />
                <Typography
                  variant='caption'
                  sx={{ color: '#475569', fontWeight: 600 }}
                >
                  Custom Software
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon sx={{ fontSize: 18, color: '#005596' }} />
                <Typography
                  variant='caption'
                  sx={{ color: '#475569', fontWeight: 600 }}
                >
                  Connected Systems
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 18, color: '#005596' }} />
                <Typography
                  variant='caption'
                  sx={{ color: '#475569', fontWeight: 600 }}
                >
                  Principal-Led Work
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Original concept illustration; not a client interface screenshot. */}
          <Grid
            item
            xs={12}
            lg={5}
          >
            <Box
              component='figure'
              sx={{
                m: 0,
                overflow: 'hidden',
                borderRadius: 4,
                border: '1px solid rgba(11, 37, 69, 0.12)',
                bgcolor: '#F8FAFC',
                boxShadow: '0 25px 50px -12px rgba(11, 37, 69, 0.16)',
              }}
            >
              {/* React 18 forwards the lowercase attribute without an unknown-prop warning. */}
              <img
                {...{ fetchpriority: 'high' }}
                sizes='(max-width: 600px) calc(100vw - 32px), (max-width: 1200px) calc(100vw - 48px), 500px'
                srcSet={`${cmsNetworkSmall} 768w, ${cmsNetworkLarge} 1536w`}
                src={cmsNetworkLarge}
                width={1536}
                height={1024}
                loading='eager'
                decoding='async'
                alt='Concept illustration of one content hub serving four distinct websites.'
                style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '3 / 2' }}
              />
              <Typography
                component='figcaption'
                variant='caption'
                sx={{ display: 'block', px: 2, py: 1.5, color: '#475569' }}
              >
                Shared publishing, distinct site identities
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Delivered examples before the capability inventory. */}
      <Card
        component='section'
        aria-labelledby='selected-work-heading'
        elevation={0}
        sx={{
          p: { xs: 3, md: 5 },
          mb: { xs: 8, md: 12 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.1)',
        }}
      >
        <Typography
          variant='caption'
          sx={{ color: '#005596', fontWeight: 700, letterSpacing: '0.08em' }}
        >
          SELECTED WORK
        </Typography>
        <Typography
          variant='h4'
          component='h2'
          id='selected-work-heading'
          sx={{ mt: 2, mb: 2, fontWeight: 800 }}
        >
          Publishing tools in practice
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
            mb: 1,
          }}
        >
          <Box component='article'>
            <Typography
              variant='body2'
              sx={{ color: '#005596', fontWeight: 700, mb: 1 }}
            >
              Delivered through our predecessor software practice
            </Typography>
            <Typography
              component='h3'
              variant='h6'
              sx={{ fontWeight: 700, mb: 1 }}
            >
              One CMS, distinct sites
            </Typography>
            <Typography sx={{ color: '#475569' }}>
              Our principal built a shared CMS that let staff manage separately branded sites with
              their own content, navigation, and editorial access, including publishing in English,
              Spanish, and French.
            </Typography>
          </Box>
          <Box component='article'>
            <Typography
              variant='body2'
              sx={{ color: '#005596', fontWeight: 700, mb: 1 }}
            >
              Delivered by Scaled Tech for The Garden Marquee
            </Typography>
            <Typography
              component='h3'
              variant='h6'
              sx={{ fontWeight: 700, mb: 1 }}
            >
              Content and inquiries together
            </Typography>
            <Typography sx={{ color: '#475569' }}>
              Scaled Tech built staff editing tools for venue pages, posts, galleries, and FAQs
              alongside an inquiry flow that checks date availability and brings requests into an
              administrative workflow.
            </Typography>
          </Box>
        </Box>
        <Button
          component={Link}
          to='/case-studies'
          sx={{ mt: 2 }}
          endIcon={<ArrowForwardIcon />}
        >
          Explore all CMS case studies
        </Button>
        <Button
          href='/cms-capabilities'
          data-track='capability_statement'
          sx={{ mt: 2 }}
          endIcon={<ArrowForwardIcon />}
        >
          View CMS capability statement
        </Button>
      </Card>

      {/* Client references: approved excerpts from signed reference letters. */}
      <Box
        component='section'
        aria-labelledby='client-words-heading'
        sx={{ mb: { xs: 8, md: 12 } }}
      >
        <Typography
          variant='h4'
          component='h2'
          id='client-words-heading'
          sx={{ mb: 3, fontWeight: 800 }}
        >
          In our clients’ words
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
          }}
        >
          {testimonials.map((testimonial) => (
            <TestimonialQuote
              key={testimonial.id}
              testimonial={testimonial}
            />
          ))}
        </Box>
      </Box>

      {/* Working principles */}
      <Box
        sx={{
          backgroundColor: '#0B2545',
          color: '#FFFFFF',
          borderRadius: 3,
          p: { xs: 4, md: 5 },
          mb: { xs: 8, md: 12 },
          border: '1px solid rgba(0, 180, 216, 0.2)',
          boxShadow: '0 10px 25px -5px rgba(11, 37, 69, 0.15)',
        }}
      >
        <Grid
          container
          spacing={4}
        >
          <Grid
            item
            xs={12}
            md={4}
          >
            <Typography
              variant='caption'
              sx={{
                color: '#4CD6FB',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 700,
              }}
            >
              Work With the Principal
            </Typography>
            <Typography
              variant='h3'
              component='h2'
              sx={{
                fontFamily: '"Manrope Variable", sans-serif',
                fontWeight: 800,
                fontSize: { xs: '2rem', sm: '2.5rem' },
                color: '#FFFFFF',
                overflowWrap: 'anywhere',
                my: 1,
              }}
            >
              Direct Collaboration
            </Typography>
            <Typography
              variant='body2'
              sx={{ color: '#CBD5E1', lineHeight: 1.6 }}
            >
              Work with the person responsible for the technical direction.
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
          >
            <Typography
              variant='caption'
              sx={{
                color: '#4CD6FB',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 700,
              }}
            >
              Fit the Workflow
            </Typography>
            <Typography
              variant='h3'
              component='h2'
              sx={{
                fontFamily: '"Manrope Variable", sans-serif',
                fontWeight: 800,
                fontSize: { xs: '2rem', sm: '2.5rem' },
                color: '#FFFFFF',
                overflowWrap: 'anywhere',
                my: 1,
              }}
            >
              Useful Connections
            </Typography>
            <Typography
              variant='body2'
              sx={{ color: '#CBD5E1', lineHeight: 1.6 }}
            >
              Bring applications, content, and data into one working process.
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
          >
            <Typography
              variant='caption'
              sx={{
                color: '#4CD6FB',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 700,
              }}
            >
              Plan for What Comes Next
            </Typography>
            <Typography
              variant='h3'
              component='h2'
              sx={{
                fontFamily: '"Manrope Variable", sans-serif',
                fontWeight: 800,
                fontSize: { xs: '2rem', sm: '2.5rem' },
                color: '#FFFFFF',
                overflowWrap: 'anywhere',
                my: 1,
              }}
            >
              Maintainable Systems
            </Typography>
            <Typography
              variant='body2'
              sx={{ color: '#CBD5E1', lineHeight: 1.6 }}
            >
              Make design and delivery choices your team can support over time.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Core capabilities */}
      <Box sx={{ mb: { xs: 8, md: 12 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'flex-end' },
            mb: 5,
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant='caption'
              sx={{
                color: '#005596',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                display: 'block',
                mb: 1,
              }}
            >
              Core Capabilities
            </Typography>
            <Typography
              variant='h2'
              component='h2'
              sx={{
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                fontWeight: 800,
                color: '#0B2545',
                letterSpacing: '-0.02em',
              }}
            >
              From the Interface to the Systems Behind It
            </Typography>
          </Box>
          <Button
            component={Link}
            to='/services'
            endIcon={<ArrowForwardIcon />}
            sx={{ color: '#005596', fontWeight: 600, px: 0 }}
          >
            Explore Services
          </Button>
        </Box>

        <Grid
          container
          spacing={3}
        >
          {capabilities.map((cap, idx) => (
            <Grid
              item
              xs={12}
              md={6}
              key={idx}
            >
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  borderRadius: 3,
                  p: { xs: 3, md: 4 },
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  backgroundColor: '#FFFFFF',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#00B4D8',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 24px -10px rgba(11, 37, 69, 0.1)',
                  },
                }}
              >
                <CardContent sx={{ p: 0, pb: '0 !important' }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2,
                      backgroundColor: '#EFF6FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                      border: '1px solid rgba(0, 85, 150, 0.15)',
                    }}
                  >
                    {cap.icon}
                  </Box>
                  <Typography
                    variant='h5'
                    component='h3'
                    sx={{
                      fontFamily: '"Manrope Variable", sans-serif',
                      fontWeight: 700,
                      color: '#0B2545',
                      mb: 1.5,
                    }}
                  >
                    {cap.title}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: '#475569', lineHeight: 1.7, mb: 3 }}
                  >
                    {cap.description}
                  </Typography>

                  {/* Tech Chips */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                    {cap.tech.map((t) => (
                      <Chip
                        key={t}
                        label={t}
                        size='small'
                        sx={{
                          backgroundColor: '#F1F5F9',
                          color: '#0B2545',
                          fontFamily: '"JetBrains Mono Variable", monospace',
                          fontWeight: 500,
                          fontSize: '11px',
                          borderRadius: 1,
                        }}
                      />
                    ))}
                  </Box>

                  {/* Bullets */}
                  <Box
                    sx={{
                      borderTop: '1px solid rgba(15, 23, 42, 0.06)',
                      pt: 2.5,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.25,
                    }}
                  >
                    {cap.bullets.map((b, bIdx) => (
                      <Box
                        key={bIdx}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#005596' }} />
                        <Typography
                          variant='body2'
                          sx={{ color: '#334155', fontSize: '13px' }}
                        >
                          {b}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Defined first engagement */}
      <Card
        component='section'
        aria-labelledby='first-engagement-heading'
        elevation={0}
        sx={{
          p: { xs: 3, md: 5 },
          mb: { xs: 4, md: 6 },
          borderRadius: 3,
          border: '1px solid rgba(11, 37, 69, 0.14)',
          backgroundColor: '#FFFFFF',
        }}
      >
        <Typography
          variant='caption'
          sx={{
            color: '#005596',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            display: 'block',
          }}
        >
          Defined First Engagement
        </Typography>
        <Typography
          variant='h4'
          component='h2'
          id='first-engagement-heading'
          sx={{ mt: 1.5, mb: 1.5, fontWeight: 800, color: '#0B2545' }}
        >
          Start with a CMS &amp; Workflow Assessment
        </Typography>
        <Typography sx={{ color: '#475569', maxWidth: '70ch', mb: 2 }}>
          Whether you are improving an existing CMS or software platform or planning a new build or
          connected workflow, begin with a fixed-scope 2 to 3 week assessment. We quote a fixed fee
          after a short call based on the systems in scope.
        </Typography>
        <Box
          component='ul'
          sx={{
            m: 0,
            mb: 3,
            pl: 3,
            color: '#475569',
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gap: 1,
            maxWidth: '70ch',
            overflowWrap: 'anywhere',
          }}
        >
          <li>Current-state workflow and editorial role map</li>
          <li>Architecture and data-flow diagram</li>
          <li>Prioritized recommendations for governance, accessibility, or integration gaps</li>
          <li>Written implementation roadmap and scoped proposal for next steps</li>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Button
            component={Link}
            to='/contact-us?subject=cms-workflow-assessment'
            data-track='assessment_request'
            variant='contained'
            endIcon={<ArrowForwardIcon />}
            sx={{
              backgroundColor: '#0B2545',
              color: '#FFFFFF',
              '&:hover': { backgroundColor: '#005596' },
            }}
          >
            Request a CMS &amp; workflow assessment
          </Button>
          <Button
            component={Link}
            to='/services#first-engagement'
            endIcon={<ArrowForwardIcon />}
          >
            See assessment details on Services
          </Button>
          <BookCallLink variant='text' />
        </Box>
      </Card>
    </Container>
  );
};

export default Home;
